import { SelectGroup } from "@appretio/ui-kit";
import React, { useCallback } from "react";
import {
  ContextDropdown,
  type ContextSelectedOptions
} from "./ContextDropdown";
import { type ContextField } from "./contextOptionsConfig";
import { useBaseContextOptions } from "./hooks/useBaseContextOptions";
import { useFilteredContextOptions } from "./hooks/useFilteredContextOptions";

const EMPTY_ARRAY: string[] = [];

interface ContextSelectorProps {
  /**
   * List of dropwdowns/context fields to display
   */
  fields: ContextField[];
  /**
   * Currently selected options for each context field
   */
  selected: ContextSelectedOptions;
  /**
   * Sets selected options for each context field
   */
  setSelected: React.Dispatch<React.SetStateAction<ContextSelectedOptions>>;
  /**
   * If true, the options list is rendered with virtualize
   * Default: false
   */
  virtualize?: boolean;
}

export function ContextSelector({
  fields,
  selected,
  setSelected
}: ContextSelectorProps) {
  const { data, isLoading } = useBaseContextOptions();

  const { data: filteredData, isLoading: filteredLoading } =
    useFilteredContextOptions({ fields, filters: selected });

  // Memoize onChange handler
  const handleSetSelected = useCallback(
    (field: ContextField, value: string[]) => {
      setSelected((prev) => ({ ...prev, [field]: value }));
    },
    [setSelected]
  );

  return (
    <SelectGroup>
      {fields.map((field) => {
        const options = data?.[field] ?? EMPTY_ARRAY;
        const filteredOptions = filteredData?.[field] ?? EMPTY_ARRAY;
        const selectedOptions = selected[field] ?? EMPTY_ARRAY;

        return (
          <ContextDropdown
            key={field}
            field={field}
            options={options}
            filteredOptions={filteredOptions}
            isLoading={isLoading || filteredLoading}
            selected={selectedOptions}
            setSelected={handleSetSelected}
          />
        );
      })}
    </SelectGroup>
  );
}
